import { t } from '../../i18n/init.js';
import { formatDistance } from '../../measure/distance.js';
import type { MeasurementsSnapshot, MeasurementUnits } from '../../measure/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface MeasurementPanelProps {
  snapshot: MeasurementsSnapshot;
  distances: readonly number[];
  raycastAvailable: boolean;
  embed: boolean;
}

export interface MeasurementPanelCallbacks {
  onReset: () => void;
  onSetUnits: (units: MeasurementUnits) => void;
  onRemoveLast: () => void;
}

const UNITS: readonly MeasurementUnits[] = ['m', 'cm', 'ft'];

interface UnitButtonRef {
  unit: MeasurementUnits;
  el: HTMLButtonElement;
}

function segmentLabel(idx: number): string {
  // Letters cycle A→Z then AA, AB... — for typical 2–10 segments this
  // is just A→B, B→C, etc. We index from 'A' = 65.
  const a = String.fromCharCode(65 + (idx % 26));
  const b = String.fromCharCode(65 + ((idx + 1) % 26));
  return `${a}→${b}`;
}

export function mountMeasurementPanel(
  root: HTMLElement,
  initial: MeasurementPanelProps,
  callbacks: MeasurementPanelCallbacks,
): ComponentInstance<MeasurementPanelProps> {
  // Wrap is mounted always (to preserve cleanup discipline) but hidden
  // when measure mode is inactive — apply() toggles display.
  const wrap = document.createElement('section');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'region');
  wrap.setAttribute('aria-label', t('viewer.measure.tool_label'));
  wrap.style.cssText = initial.embed
    ? 'position:fixed;bottom:1rem;left:50%;transform:translateX(-50%);z-index:11;pointer-events:auto;display:none'
    : 'position:fixed;bottom:3rem;left:50%;transform:translateX(-50%);z-index:11;pointer-events:auto;display:none';

  // Hint pill above the panel: announces "Misura attiva · click su 2
  // punti del modello" via role=status (screen reader friendly).
  const hint = document.createElement('div');
  hint.setAttribute('role', 'status');
  hint.setAttribute('aria-live', 'polite');
  hint.style.cssText =
    'background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(245,158,11,0.30);padding:0.375rem 0.75rem;border-radius:9999px;display:flex;gap:0.375rem;font-family:system-ui,sans-serif;font-size:0.6875rem;color:#fcd34d;align-items:center;justify-content:center;margin-bottom:0.5rem';
  const hintDot = document.createElement('span');
  hintDot.setAttribute('aria-hidden', 'true');
  hintDot.style.cssText =
    'display:inline-block;width:0.375rem;height:0.375rem;border-radius:9999px;background:#f59e0b';
  hint.appendChild(hintDot);
  const hintLabel = document.createElement('span');
  hintLabel.style.cssText =
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;text-transform:uppercase;letter-spacing:0.05em';
  hintLabel.textContent = t('viewer.measure.hint_badge');
  hint.appendChild(hintLabel);
  const hintSep = document.createElement('span');
  hintSep.setAttribute('aria-hidden', 'true');
  hintSep.style.cssText = 'color:#71717a';
  hintSep.textContent = '·';
  hint.appendChild(hintSep);
  const hintText = document.createElement('span');
  hintText.style.cssText = 'color:#fef3c7';
  hintText.textContent = t('viewer.measure.hint_initial');
  hint.appendChild(hintText);
  wrap.appendChild(hint);

  // Main panel: measure list + units toggle + reset button.
  const panel = document.createElement('div');
  panel.style.cssText =
    'background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.10);padding:0.5rem 0.875rem;border-radius:1rem;display:flex;align-items:center;gap:0.625rem;font-family:system-ui,sans-serif;color:#f4f4f5;box-shadow:0 12px 32px rgba(0,0,0,0.40)';

  // List slot — populated dynamically in apply().
  const list = document.createElement('div');
  list.style.cssText =
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;display:flex;gap:0.5rem;align-items:center;flex-wrap:wrap;max-width:24rem';
  panel.appendChild(list);

  const sep1 = document.createElement('span');
  sep1.setAttribute('aria-hidden', 'true');
  sep1.style.cssText = 'width:1px;height:1.25rem;background:rgba(255,255,255,0.10)';
  panel.appendChild(sep1);

  // Units radiogroup.
  const unitsGroup = document.createElement('div');
  unitsGroup.setAttribute('role', 'radiogroup');
  unitsGroup.setAttribute('aria-label', t('viewer.measure.units_group_label'));
  unitsGroup.style.cssText =
    'display:flex;background:rgba(255,255,255,0.05);border-radius:9999px;padding:0.125rem;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem';
  const unitButtons: UnitButtonRef[] = [];
  for (const unit of UNITS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.classList.add('viewer-ui-tap-target');
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', 'false');
    b.setAttribute('aria-label', t('viewer.measure.unit_btn_label', { unit }));
    b.dataset['unit'] = unit;
    b.textContent = unit;
    b.style.cssText =
      'background:transparent;color:#a1a1aa;border:0;padding:0.25rem 0.625rem;border-radius:9999px;cursor:pointer;font-family:inherit;font-size:inherit';
    b.addEventListener('click', () => callbacks.onSetUnits(unit));
    unitsGroup.appendChild(b);
    unitButtons.push({ unit, el: b });
  }
  panel.appendChild(unitsGroup);

  const sep2 = document.createElement('span');
  sep2.setAttribute('aria-hidden', 'true');
  sep2.style.cssText = 'width:1px;height:1.25rem;background:rgba(255,255,255,0.10)';
  panel.appendChild(sep2);

  // Step 7 #5 fix: explicit "Annulla ultimo" button (Matterport / FARO
  // pattern). Backspace shortcut still works in parallel — many users
  // don't know about keyboard undo.
  const undoBtn = document.createElement('button');
  undoBtn.type = 'button';
  undoBtn.classList.add('viewer-ui-tap-target');
  undoBtn.setAttribute('aria-label', t('viewer.measure.undo_label'));
  undoBtn.title = t('viewer.measure.undo_label');
  undoBtn.textContent = t('viewer.measure.undo_btn');
  undoBtn.style.cssText =
    'background:transparent;color:#a1a1aa;border:0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;cursor:pointer;padding:0.25rem 0.5rem;border-radius:9999px';
  undoBtn.addEventListener('click', () => callbacks.onRemoveLast());
  panel.appendChild(undoBtn);

  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.classList.add('viewer-ui-tap-target');
  resetBtn.setAttribute('aria-label', t('viewer.measure.reset_label'));
  resetBtn.textContent = t('viewer.measure.reset_btn');
  resetBtn.style.cssText =
    'background:transparent;color:#a1a1aa;border:0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;cursor:pointer;padding:0.25rem 0.5rem;border-radius:9999px';
  resetBtn.addEventListener('click', () => callbacks.onReset());
  panel.appendChild(resetBtn);

  wrap.appendChild(panel);

  // Disclaimer below the panel.
  const disclaimer = document.createElement('p');
  disclaimer.style.cssText =
    'margin:0.5rem 0 0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.625rem;color:#71717a;text-align:center';
  disclaimer.textContent = t('viewer.measure.disclaimer');
  wrap.appendChild(disclaimer);

  // Warning slot for raycastAvailable=false.
  const warning = document.createElement('p');
  warning.setAttribute('role', 'note');
  warning.style.cssText =
    'margin:0.25rem 0 0;background:rgba(127,29,29,0.85);color:#fee2e2;font-size:0.625rem;text-align:center;padding:0.25rem 0.5rem;border-radius:0.5rem;display:none;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  warning.textContent = t('viewer.measure.raycast_warning');
  wrap.appendChild(warning);

  root.appendChild(wrap);

  const apply = (props: MeasurementPanelProps): void => {
    wrap.style.display = props.snapshot.active ? 'block' : 'none';
    if (!props.snapshot.active) return;

    // Step 7 #4 fix: dynamic hint reflecting how many points are placed,
    // matching the Matterport / Polycam pattern (clear next-action prompt).
    const n = props.snapshot.pointCount;
    if (n === 0) {
      hintText.textContent = t('viewer.measure.hint_step0');
    } else if (n === 1) {
      hintText.textContent = t('viewer.measure.hint_step1');
    } else {
      hintText.textContent = t('viewer.measure.hint_stepN');
    }

    // Units radiogroup state.
    for (const b of unitButtons) {
      const checked = b.unit === props.snapshot.units;
      b.el.setAttribute('aria-checked', checked ? 'true' : 'false');
      b.el.style.cssText = checked
        ? 'background:rgba(245,158,11,0.20);color:#fcd34d;border:0;padding:0.25rem 0.625rem;border-radius:9999px;cursor:pointer;font-family:inherit;font-size:inherit'
        : 'background:transparent;color:#a1a1aa;border:0;padding:0.25rem 0.625rem;border-radius:9999px;cursor:pointer;font-family:inherit;font-size:inherit';
    }

    // List of measurements.
    list.innerHTML = '';
    if (props.distances.length === 0) {
      const empty = document.createElement('span');
      empty.style.cssText = 'color:#71717a';
      empty.textContent = t('viewer.measure.empty');
      list.appendChild(empty);
    } else {
      props.distances.forEach((d, idx) => {
        const item = document.createElement('span');
        item.style.cssText = 'display:inline-flex;gap:0.375rem;align-items:center';
        const label = document.createElement('span');
        label.style.cssText = 'color:#a1a1aa';
        label.textContent = segmentLabel(idx);
        const value = document.createElement('span');
        value.style.cssText = 'color:#fcd34d;font-weight:700';
        setText(value, formatDistance(d, props.snapshot.units));
        item.appendChild(label);
        item.appendChild(value);
        list.appendChild(item);
      });
    }

    warning.style.display = props.raycastAvailable ? 'none' : 'block';
  };

  apply(initial);

  return {
    update(props: MeasurementPanelProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
